import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  ArrowLeft, ArrowRight, Package, Send, Trophy, Truck, CheckCircle2, Sparkles, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

export default function NewOrder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: product, 2: pick suppliers, 3: compare quotes, 4: confirm

  const [product, setProduct] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("pcs");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [picked, setPicked] = useState({});
  const [loadingSup, setLoadingSup] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [priceRequest, setPriceRequest] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [assignEmployeeId, setAssignEmployeeId] = useState("");

  useEffect(() => {
    apiClient.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}).finally(() => setLoadingSup(false));
    apiClient.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    apiClient.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const togglePick = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const pickedIds = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

  const goStep1 = (e) => {
    e.preventDefault();
    if (!product.trim() || !quantity || Number(quantity) <= 0) { toast.error("Enter product and quantity"); return; }
    setStep(2);
  };

  const sendRequest = async () => {
    if (pickedIds.length === 0) { toast.error("Select at least one supplier"); return; }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/price-requests", {
        product: product.trim(),
        quantity: Number(quantity),
        unit,
        supplier_ids: pickedIds,
      });
      setPriceRequest(data);
      setSelectedQuote(data.quotes.find((q) => q.supplier_id === data.best_supplier_id));
      setStep(3);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  const placeOrder = async () => {
    if (!selectedQuote) return;
    setSubmitting(true);
    try {
      const payload = {
        product: priceRequest.product,
        quantity: priceRequest.quantity,
        unit: priceRequest.unit,
        supplier_id: selectedQuote.supplier_id,
        price: selectedQuote.price,
        customer_id: customerId || null,
        price_request_id: priceRequest.id,
      };
      const { data: order } = await apiClient.post("/orders", payload);
      if (assignEmployeeId) {
        try {
          await apiClient.post("/tasks", {
            title: `Fulfill: ${order.product}`,
            description: `Order placed with ${order.supplier_name} · ₹${order.price.toFixed(2)}`,
            employee_id: assignEmployeeId,
            order_id: order.id,
          });
          toast.success("Order placed & task assigned");
        } catch {
          toast.warning("Order placed, but task assignment failed");
        }
      } else {
        toast.success("Order placed");
      }
      navigate("/orders");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <AppHeader title="New Order" subtitle={`Step ${step} of 3`} />
      <div className="px-4 pt-4 pb-28">
        <button onClick={() => (step === 1 ? navigate(-1) : setStep(step - 1))} data-testid="order-back"
          className="flex items-center gap-1 text-sm font-semibold text-slate-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-2 mb-5">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1.5 flex-1 rounded-full ${step >= n ? "bg-emerald-600" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <form onSubmit={goStep1} data-testid="step-product" className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center gap-3 pb-2">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><Package className="w-6 h-6" /></div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">What do you need?</h3>
                <p className="text-xs text-slate-500">Product details for price request</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Product *</Label>
              <Input data-testid="product-input" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g., Basmati Rice 25kg" required className="h-14 rounded-xl text-base" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1.5">
                <Label>Quantity *</Label>
                <Input data-testid="qty-input" type="number" min="0" step="0.01" value={quantity} onChange={(e) => setQuantity(e.target.value)} required className="h-14 rounded-xl text-base" />
              </div>
              <div className="space-y-1.5">
                <Label>Unit</Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger data-testid="unit-select" className="h-14 rounded-xl text-base"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["pcs", "kg", "ltr", "box", "bag"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {customers.length > 0 && (
              <div className="space-y-1.5">
                <Label>For Customer (optional)</Label>
                <Select value={customerId || "none"} onValueChange={(v) => setCustomerId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="customer-select" className="h-14 rounded-xl text-base"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No customer</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.phone})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button type="submit" data-testid="next-step-1" className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-lg font-semibold gap-2">
              Next <ArrowRight className="w-5 h-5" />
            </Button>
          </form>
        )}

        {step === 2 && (
          <div data-testid="step-suppliers" className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center gap-3 pb-2">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center"><Truck className="w-6 h-6" /></div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Pick suppliers</h3>
                <p className="text-xs text-slate-500">We'll request prices from each</p>
              </div>
            </div>
            {loadingSup ? (
              <div className="text-slate-500 text-sm py-6 text-center">Loading...</div>
            ) : suppliers.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-700 font-semibold mb-1">No suppliers yet</p>
                <p className="text-sm text-slate-500 mb-4">Add suppliers first</p>
                <Button onClick={() => navigate("/suppliers/new")} className="rounded-xl bg-emerald-600 hover:bg-emerald-700">Add Supplier</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {suppliers.map((s) => (
                  <label key={s.id} data-testid={`pick-supplier-${s.id}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${picked[s.id] ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
                    <Checkbox checked={!!picked[s.id]} onCheckedChange={() => togglePick(s.id)} className="h-5 w-5" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500">{s.phone}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <Button onClick={sendRequest} disabled={submitting || pickedIds.length === 0} data-testid="send-request-btn"
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-lg font-semibold gap-2">
              <Send className="w-5 h-5" /> {submitting ? "Sending..." : `Get Quotes (${pickedIds.length})`}
            </Button>
          </div>
        )}

        {step === 3 && priceRequest && (
          <div data-testid="step-compare" className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-emerald-700" />
              <div>
                <div className="text-sm font-bold text-emerald-900">Quotes received!</div>
                <div className="text-xs text-emerald-700">{priceRequest.product} · {priceRequest.quantity} {priceRequest.unit}</div>
              </div>
            </div>
            <div className="space-y-2" data-testid="quotes-list">
              {[...priceRequest.quotes].sort((a, b) => a.price - b.price).map((q) => {
                const isBest = q.supplier_id === priceRequest.best_supplier_id;
                const isSelected = selectedQuote?.supplier_id === q.supplier_id;
                return (
                  <button
                    key={q.supplier_id}
                    data-testid={`quote-${q.supplier_id}`}
                    onClick={() => setSelectedQuote(q)}
                    className={`w-full text-left p-4 rounded-2xl border-2 transition ${
                      isSelected ? "border-emerald-600 bg-emerald-50" : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-slate-900">{q.supplier_name}</span>
                          {isBest && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full"><Trophy className="w-3 h-3" /> Best</span>}
                        </div>
                        {q.note && <div className="text-xs text-slate-500 mt-1">{q.note}</div>}
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-slate-900">₹{q.price.toFixed(2)}</div>
                        {isSelected && <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto mt-1" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {employees.length > 0 && (
              <div data-testid="assign-employee-section" className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-bold text-slate-900">Assign to employee</span>
                  <span className="text-xs text-slate-400 ml-auto">optional</span>
                </div>
                <p className="text-xs text-slate-500">Auto-create a task for this order and assign it to your staff.</p>
                <Select value={assignEmployeeId || "none"} onValueChange={(v) => setAssignEmployeeId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="assign-employee-select" className="h-12 rounded-xl text-base">
                    <SelectValue placeholder="No one — assign later" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No one — assign later</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}{e.role ? ` · ${e.role}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={placeOrder} disabled={!selectedQuote || submitting} data-testid="place-order-btn"
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-lg font-semibold gap-2 mt-3">
              {submitting ? "Placing..." : `Place Order with ${selectedQuote?.supplier_name || "..."}`}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
